import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import 'leaflet.markercluster'

export default class extends Controller {
  static targets = ["container", "popupTemplate", "loadingContainer", "loadingIndicator", "radiusSelect", "errorContainer", "zoomInButton", "zoomOutButton"]
  static values = {
    zoom: { type: Number, default: 13 },
    currentLat: { type: Number, default: 37.7749 },
    currentLng: { type: Number, default: -122.4194 },
    userInteraction: { type: Boolean, default: true },
    contentFilter: String,
    sourceFilter: String
  }
  
  populateFromURLorStorage(paramName, storageKey, defaultValue = null) {
    const urlParams = new URLSearchParams(window.location.search)
    const urlValue = urlParams.get(paramName)
    if (urlValue) {
      console.debug(`Found ${paramName} in URL parameters: `, urlValue)
      
      if (isNaN(parseFloat(urlValue))) {
        console.debug(`Invalid ${paramName} in URL parameters: `, urlValue)
        return defaultValue
      } else {
        localStorage.setItem(storageKey, urlValue)
        return parseFloat(urlValue)
      }
    } else {
      const storedValue = localStorage.getItem(storageKey)
      if (storedValue && !isNaN(parseFloat(storedValue))) {
        console.debug(`Using stored ${paramName} from localStorage: `, storedValue)
        return parseFloat(storedValue)
      } else {
        return defaultValue
      }
    }
  }

  async connect() {
    console.debug("Map controller connected")
    this.programmaticMove = false // Flag to track programmatic map movements
    this.popupMove = false // Flag to track popup-induced map movements
    this.isScoutMode = this.contentFilterValue === 'scout'
    this.locationWatchId = null
    this.currentLocationMarker = null
    this.discoveryMarkers = new Map() // Track discovery ID -> marker mappings

    // Check for lat/lng in URL parameters and store in localStorage
    this.currentLatValue = this.populateFromURLorStorage('lat', 'seeniq_map_current_lat')
    this.currentLngValue = this.populateFromURLorStorage('lng', 'seeniq_map_current_lng')
    this.zoomValue = this.populateFromURLorStorage('zoom', 'seeniq_map_zoom', this.zoomValue)
    
    if (this.isScoutMode || !(this.currentLatValue && this.currentLngValue)) {
      try {
        const position = await window.getCurrentPosition(true) // true for high accuracy
        console.debug("User location obtained from global function", position.coords)
        this.currentLatValue = position.coords.latitude
        this.currentLngValue = position.coords.longitude
      } catch (error) {
        console.log("Error getting location: ", error)
      }
    }

    this.initializeFilterButtons()
    this.initializeMap()
    
    if (this.isScoutMode) {
      // Center map on new location
      this.programmaticMove = true
      this.map.setView([this.currentLatValue, this.currentLngValue], this.zoomValue)

      this.updateScoutLocation(this.currentLatValue, this.currentLngValue)
      
      // Register for position updates from the global system
      this.positionUpdateCallback = window.onPositionUpdate((position) => {
        console.debug("Location update received from global system:", position.coords)
        if (this.currentLatValue === position.coords.latitude && this.currentLngValue === position.coords.longitude) {
          console.debug("Location unchanged, skipping update")
          return
        }
        this.updateScoutLocation(position.coords.latitude, position.coords.longitude)
      })
    } else {
      this.loadDiscoveries()
    }
  }

  initializeFilterButtons() {
    // Use server-side filter values instead of URL parameters
    const currentContentFilter = this.contentFilterValue
    const currentSourceFilter = this.sourceFilterValue

    // Update button states
    const filterButtons = document.querySelectorAll('.tabs.is-toggle li')
    filterButtons.forEach(button => {
      const href = button.querySelector("a").getAttribute('href')
      if (href) {
        const buttonUrl = new URL(href, window.location.origin)
        const buttonContentFilter = buttonUrl.searchParams.get('content_filter')
        const buttonSourceFilter = buttonUrl.searchParams.get('source_filter')

        if (buttonContentFilter === currentContentFilter && buttonSourceFilter === currentSourceFilter) {
          button.classList.add('is-active')
        } else {
          button.classList.remove('is-active')
        }
      }
    })
  }

  disconnect() {
    console.debug("Map controller disconnected")

    // Stop location watching in scout mode
    this.stopLocationWatching()

    if (this.map) {
      // Remove current location marker if it exists
      if (this.currentLocationMarker) {
        this.map.removeLayer(this.currentLocationMarker)
        this.currentLocationMarker = null
      }

      // Remove marker cluster if it exists
      if (this.markerCluster) {
        this.map.removeLayer(this.markerCluster)
        this.markerCluster = null
      }

      // Remove the map and cleanup
      this.map.remove()
      this.map = null
    }

    // Clear discovery markers tracking
    if (this.discoveryMarkers) {
      this.discoveryMarkers.clear()
      this.discoveryMarkers = null
    }
  }

  initializeMap() {
    console.debug("Map initialization started")
    // Check if map is already initialized
    if (this.map) {
      return
    }

    const enableInteraction = this.userInteractionValue

    // Initialize map
    this.map = L.map(this.containerTarget, {
      dragging: enableInteraction,
      touchZoom: enableInteraction,
      scrollWheelZoom: enableInteraction,
      doubleClickZoom: enableInteraction,
      boxZoom: enableInteraction,
      tap: enableInteraction,
      keyboard: enableInteraction,
      zoomControl: false
    })

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map)
    
    this.map.setView([this.currentLatValue, this.currentLngValue], this.zoomValue)
    
    // Add event listener for map pan/zoom to update current location and reload discoveries
    this.map.on('moveend', () => {
      // Skip if this was a programmatic move or popup-induced move
      if (this.programmaticMove) {
        this.programmaticMove = false
        return
      }

      if (this.popupMove) {
        this.popupMove = false
        return
      }
      
      if (!this.isScoutMode) {
        const center = this.map.getCenter()
        this.currentLatValue = center.lat
        this.currentLngValue = center.lng
        // Store current coordinates and zoom in localStorage
        localStorage.setItem('seeniq_map_current_lat', this.currentLatValue.toString())
        localStorage.setItem('seeniq_map_current_lng', this.currentLngValue.toString())
      }
      
      const zoom = this.map.getZoom()
      this.zoomValue = zoom
      localStorage.setItem('seeniq_map_zoom', zoom.toString())

      this.loadDiscoveriesInBounds()
    })

    // Add event listeners for popup events
    this.map.on('popupopen', () => {
      // Set flag before popup potentially moves the map
      this.popupMove = true
      // Clear the flag after a short delay to allow for map adjustment
      setTimeout(() => {
        this.popupMove = false
      }, 500)
    })
  }

  async loadDiscoveries() {
    // this.showLoading()
    this.hideError()
    this.loadDiscoveriesInBounds()
  }

  updateScoutLocation(lat, lng) {
    console.debug("Updating scout location:", { lat, lng })

    // Update current location values
    this.currentLatValue = lat
    this.currentLngValue = lng

    // Update current location marker
    this.updateCurrentLocationMarker(lat, lng)

    // Load discoveries for new location
    this.loadDiscoveriesInBounds()
  }

  updateCurrentLocationMarker(lat, lng) {
    console.debug("Updating current location marker at:", { lat, lng })

    // Remove existing marker if it exists
    if (this.currentLocationMarker) {
      this.map.removeLayer(this.currentLocationMarker)
    }

    // Create animated current location marker with inline styles as fallback
    const pulsingIcon = L.divIcon({
      className: 'current-location-marker',
      html: `
        <div class="pulse" style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: rgba(0, 150, 255, 0.3);
          animation: pulse-location 2s infinite;
        "></div>
        <div class="dot" style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background-color: #0096ff;
          border: 2px solid white;
          box-shadow: 0 0 10px rgba(0, 150, 255, 0.8);
          z-index: 10;
        "></div>
        <style>
          @keyframes pulse-location {
            0% {
              transform: translate(-50%, -50%) scale(0.8);
              opacity: 1;
            }
            100% {
              transform: translate(-50%, -50%) scale(2);
              opacity: 0;
            }
          }
        </style>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    })

    this.currentLocationMarker = L.marker([lat, lng], { icon: pulsingIcon, zIndexOffset: 10000 })
    this.currentLocationMarker.addTo(this.map)
    console.debug("Current location marker added to map")
  }

  stopLocationWatching() {
    if (this.positionUpdateCallback) {
      window.offPositionUpdate(this.positionUpdateCallback)
      this.positionUpdateCallback = null
      console.debug("Stopped location watching - unregistered from global system")
    }
  }

  loadDiscoveriesInBounds() {
    const bounds = this.map.getBounds()
    const north = bounds.getNorth()
    const south = bounds.getSouth()
    const east = bounds.getEast()
    const west = bounds.getWest()

    console.debug(`Loading discoveries in bounds: N=${north}, S=${south}, E=${east}, W=${west}`)

    // Build URL with query parameters
    const url = new URL(window.location.origin + '/discoveries/index_map')
    url.searchParams.append('north', north)
    url.searchParams.append('south', south)
    url.searchParams.append('east', east)
    url.searchParams.append('west', west)

    // Use server-side filter values instead of URL parameters
    const contentFilter = this.contentFilterValue
    const sourceFilter = this.sourceFilterValue
    url.searchParams.append('content_filter', contentFilter)
    url.searchParams.append('source_filter', sourceFilter)

    // In scout mode, send user's current location
    if (this.isScoutMode && this.currentLatValue && this.currentLngValue) {
      url.searchParams.append('user_lat', this.currentLatValue)
      url.searchParams.append('user_lng', this.currentLngValue)
    }

    this.fetchDiscoveries(url)
  }

  fetchDiscoveries(url) {
    // Fetch discoveries
    fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })
    .then(response => response.json())
    .then(discoveries => {
      this.renderDiscoveries(discoveries)
      // Hide loading indicator if it exists
      if (this.hasLoadingContainerTarget) {
        this.loadingContainerTarget.classList.add('is-hidden')
      }
    })
    .catch(error => {
      console.error("Error fetching discoveries:", error)
      this.showError()
    })
  }

  renderDiscoveries(discoveries) {
    console.debug(`Rendering ${discoveries.length} discoveries on map`)

    if (!this.map) {
      console.error("Map not initialized, cannot render discoveries")
      return
    }

    if (!discoveries || !discoveries.length) {
      console.log("No discoveries to display on map")
      // Remove all existing markers since there are no discoveries
      this.discoveryMarkers.forEach((marker, discoveryId) => {
        this.markerCluster.removeLayer(marker)
      })
      this.discoveryMarkers.clear()
      return
    }

    // Initialize marker cluster group if it doesn't exist
    if (!this.markerCluster) {
      this.markerCluster = L.markerClusterGroup({
        showCoverageOnHover: false,
        maxClusterRadius: 50,
        spiderfyOnMaxZoom: true,
        // Custom clustering options
        iconCreateFunction: function(cluster) {
          const count = cluster.getChildCount();
          return L.divIcon({
            html: `<div class="cluster-icon">${count}</div>`,
            className: 'custom-cluster-icon',
            iconSize: L.point(40, 40)
          });
        }
      });
      this.map.addLayer(this.markerCluster);
    }

    // Create a Set of current discovery IDs for efficient lookup
    const currentDiscoveryIds = new Set(discoveries.map(d => d.id))

    // Remove markers for discoveries that are no longer present
    this.discoveryMarkers.forEach((marker, discoveryId) => {
      if (!currentDiscoveryIds.has(discoveryId)) {
        this.markerCluster.removeLayer(marker)
        this.discoveryMarkers.delete(discoveryId)
        console.debug(`Removed marker for discovery ${discoveryId}`)
      }
    })

    // Add markers for new discoveries
    discoveries.forEach(discovery => {
      // Skip if marker already exists for this discovery
      if (this.discoveryMarkers.has(discovery.id)) {
        return
      }

      // Create marker
      const marker = L.marker([discovery.latitude, discovery.longitude]);

      // Create popup content if the template is available
      if (this.hasPopupTemplateTarget) {
        const popupTemplate = this.popupTemplateTarget.cloneNode(true)
        popupTemplate.classList.remove('is-hidden')

        const popupImage = popupTemplate.querySelector('.popup-image')
        if (popupImage) {
          popupImage.src = discovery.photo_url || '/placeholder-image.jpg'
        }

        const popupTitle = popupTemplate.querySelector('.popup-title')
        if (popupTitle) {
          popupTitle.textContent = discovery.title
        }

        // Populate owner information
        const ownerAvatarContainer = popupTemplate.querySelector('.popup-owner-avatar')
        const ownerNameContainer = popupTemplate.querySelector('.popup-owner-name')

        if (ownerAvatarContainer && ownerNameContainer) {
          // Create avatar element similar to _avatar.html.haml
          if (discovery.owner_avatar_url) {
            ownerAvatarContainer.innerHTML = `<img src="${discovery.owner_avatar_url}" style="border-radius: 50%; width: 32px; height: 32px;" />`
          } else {
            ownerAvatarContainer.innerHTML = `
              <div style="border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 13px; background-color: #6c757d; color: white;">
                ${discovery.owner_initials}
              </div>
            `
          }
          ownerNameContainer.textContent = discovery.owner_name || 'Unknown User'
        }

        const viewButton = popupTemplate.querySelector('.view-button')
        if (viewButton) {
          viewButton.href = '/discoveries/' + discovery.id
        }

        // Bind popup to marker
        marker.bindPopup(popupTemplate.innerHTML)
      }

      // Add the marker to the cluster group and track it
      this.markerCluster.addLayer(marker);
      this.discoveryMarkers.set(discovery.id, marker)
      console.debug(`Added marker for discovery ${discovery.id}`)
    })
  }

  tryAgain() {
    this.loadDiscoveries()
  }

  async centerOnCurrentLocation() {
    console.debug("Centering map on current location")

    try {
      const position = await window.getCurrentPosition(true) // true for high accuracy
      console.debug("Got current position", position.coords)
      const lat = position.coords.latitude
      const lng = position.coords.longitude

      // Center the map on the new location with a smooth animation
      this.programmaticMove = false
      this.map.setView([lat, lng], this.zoomValue, {
        animate: true,
        duration: 1.0
      })
    } catch (error) {
      console.error("Error getting current location:", error)
      // Show a brief error message or fallback behavior
      alert("Unable to get your current location. Please check your location permissions.")
    }
  }

  zoomIn() {
    if (this.map) {
      this.map.zoomIn()
    }
  }

  zoomOut() {
    if (this.map) {
      this.map.zoomOut()
    }
  }

  showLoading() {
    this.hideError()
    this.loadingContainerTarget.classList.remove('is-hidden')
  }

  hideLoading() {
    if (this.hasLoadingContainerTarget) {
      this.loadingContainerTarget.classList.add('is-hidden')
    }
  }

  showError() {
    this.hideLoading()
    this.errorContainerTarget.classList.remove('is-hidden')
  }

  hideError() {
    this.errorContainerTarget.classList.add('is-hidden')
  }
}
